import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';


function MonthlyAttendance() {
    const navigate = useNavigate();
    const [attendanceData, setAttendanceData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [teacherType, setTeacherType] = useState('ALL');
    const [teacherTypeList, setTeacherTypeList] = useState([]);
    const [teacherId, setTeacherId] = useState('');
    const [formattedMonth, setFormattedMonth] = useState('');
    const [dayStyles, setDayStyles] = useState({});
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [emailDialogVisible, setEmailDialogVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [editTidDialogVisible, setEditTidDialogVisible] = useState(false);
    const [pinDialogVisible, setPinDialogVisible] = useState(false);
    const [pin, setPin] = useState("");
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [newTid, setNewTid] = useState("");
    const tableWrapperRef = useRef(null);
    const scrollBarRef = useRef(null);
    const [tableWidth, setTableWidth] = useState(0);
    
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 50,
        page: 0,
        sortField: 'tid',
        sortOrder: 1
    });

    // Sync horizontal scroll between custom scrollbar and table
    useEffect(() => {
        const syncScroll = () => {
            const tableWrapper = document.querySelector(".data-panel .p-datatable-wrapper");
            const scrollBar = scrollBarRef.current;
            if (tableWrapper && scrollBar) {
                setTableWidth(tableWrapper.scrollWidth);
                scrollBar.onscroll = () => {
                    tableWrapper.scrollLeft = scrollBar.scrollLeft;
                };
                tableWrapper.onscroll = () => {
                    scrollBar.scrollLeft = tableWrapper.scrollLeft;
                };
            }
        };
        const timer = setTimeout(syncScroll, 500);
        return () => clearTimeout(timer);
    }, [attendanceData]);

    const fetchTeacherTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher-types`);
            const types = response.data;
            setTeacherTypeList(types);
        } catch (error) {
            console.error('Error fetching teacher types:', error);
            setTeacherTypeList([{ label: 'ALL', value: 'ALL' }]);
        }
    }, []);

    const fetchMonthInfo = useCallback(async (year, month) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/month-info`, {
                params: { year, month }
            });
            setFormattedMonth(response.data.formattedMonth);
            setDayStyles(response.data.dayStyles);
            setDaysInMonth(response.data.daysInMonth);
        } catch (error) {
            console.error('Error fetching month info:', error);
            const date = new Date(year, month - 1, 1);
            setFormattedMonth(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
            setDaysInMonth(new Date(year, month, 0).getDate());
        }
    }, []);

    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        try {
            const year = selectedMonth.getFullYear();
            const month = selectedMonth.getMonth() + 1;
            
            const response = await axios.get(`${API_BASE_URL}/attendance/monthly`, {
                params: {
                    year,
                    month,
                    teacherType: teacherType !== 'ALL' ? teacherType : null,
                    teacherId: teacherId || null,
                    page: lazyParams.page,
                    pageSize: lazyParams.rows,
                    sortField: lazyParams.sortField,
                    sortOrder: lazyParams.sortOrder
                }
            });
            
            setAttendanceData(response.data.data || []);
            setTotalRecords(response.data.totalRecords || 0);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            setAttendanceData([]);
            setTotalRecords(0);
            const msg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Failed to load attendance data. Check that the API server is running.\n\nError: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, teacherType, teacherId, lazyParams]);

    useEffect(() => {
        fetchTeacherTypes();
    }, [fetchTeacherTypes]);

    useEffect(() => {
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        fetchMonthInfo(year, month);
        fetchAttendanceData();
    }, [selectedMonth, fetchMonthInfo, fetchAttendanceData]);

    const onPage = (event) => {
        setLazyParams({
            ...lazyParams,
            first: event.first,
            rows: event.rows,
            page: event.page
        });
    };

    const onSort = (event) => {
        setLazyParams({
            ...lazyParams,
            sortField: event.sortField,
            sortOrder: event.sortOrder,
            first: 0,
            page: 0
        });
    };

    const handleSearch = () => {
        setLazyParams({
            ...lazyParams,
            first: 0,
            page: 0
        });
    };

    const handleTeacherTypeChange = (e) => {
        setTeacherType(e.value);
        setLazyParams({
            ...lazyParams,
            first: 0,
            page: 0
        });
    };

    const getDayStyleClass = (day) => {
        return dayStyles[day] || '';
    };

    const handleExport = () => {
        setEmailDialogVisible(true);
    };

    const handleSendEmail = async () => {
        try {
            setLoading(true);
            const year = selectedMonth.getFullYear();
            const month = selectedMonth.getMonth() + 1;
            
            await axios.post(`${API_BASE_URL}/attendance/export`, {
                year,
                month,
                teacherType,
                teacherId,
                email
            });
            
            setEmailDialogVisible(false);
            setEmail('');
            alert('Report sent successfully!');
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Error sending report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditTidClick = (rowData) => {
        setEditingTeacher(rowData);
        setNewTid(rowData.teacherSignatureId || "");
        setPinDialogVisible(true);
    };

    const handlePinSubmit = () => {
        if (pin === "0218") {
            setPinDialogVisible(false);
            setPin("");
            setEditTidDialogVisible(true);
        } else {
            alert("Invalid PIN");
            setPin("");
        }
    };

    const handleUpdateTid = async () => {
        try {
            setLoading(true);
            await axios.put(`${API_BASE_URL}/teacher/update-tid`, {
                teacherId: editingTeacher.teacherSignatureId,
                newSignatureId: newTid,
                pin: "0218"
            });
            setEditTidDialogVisible(false);
            setEditingTeacher(null);
            setNewTid("");
            alert("Sign ID (TID) updated successfully!");
            fetchAttendanceData();
        } catch (error) {
            console.error("Error updating TID:", error);
            alert("Error updating Sign ID (TID). Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const tidTemplate = (rowData) => {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span>{rowData.teacherSignatureId}</span>
                <i
                    className="pi pi-pencil"
                    style={{ cursor: "pointer", fontSize: "0.8rem", color: "#007bff" }}
                    onClick={(e) => { e.stopPropagation(); handleEditTidClick(rowData); }}
                    title="Edit TID"
                />
            </div>
        );
    };

    const lastPunchDateTemplate = (rowData) => {
        if (!rowData.lastPunchDate) return '-';
        const d = new Date(rowData.lastPunchDate);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const teacherNameTemplate = (rowData) => {
        return (
            <span
                style={{ cursor: "pointer", color: "#2266e3", textDecoration: "underline" }}
                onClick={(e) => {
                    e.stopPropagation();
                    const monthStr = selectedMonth.toISOString().slice(0, 7); window.open(`/teacher-report/${rowData.teacherSignatureId}?name=${encodeURIComponent(rowData.teacherName)}&month=${monthStr}`, "_blank");
                }}
            >
                {rowData.teacherName}
            </span>
        );
    };


    const rowIndexTemplate = (rowData, column) => {
        return lazyParams.first + column.rowIndex + 1;
    };

    const dayColumns = [];
    for (let day = 1; day <= 31; day++) {
        const dayStr = day.toString().padStart(2, '0');
        const fieldName = `day${dayStr}`;
        dayColumns.push(
            <Column
                key={day}
                field={fieldName}
                header={dayStr}
                className={`day-column ${getDayStyleClass(day)}`}
                headerClassName={getDayStyleClass(day)}
                style={{ textAlign: 'center', verticalAlign: 'top', minWidth: '35px' }}
            />
        );
    }

    return (
        <div className="card p-fluid">
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            <div className="p-grid p-justify-center" style={{ marginTop: '0', textAlign: 'center', overflow: 'hidden' }}><div className="monthly-fixed-header" style={{ width: '100%' }}>
                <h1 className="page-title" style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#f8f9fa', padding: '5px 10px', margin: 0 }}>
                    TEACHERS MONTHLY ATTENDANCE OVERVIEW REPORT OF ANANDA COLLEGE {formattedMonth}
                </h1>

                <Panel className="search-panel" style={{ width: '100%',  }}>
                    <div className="filter-row">
                        <div className="filter-item">
                            <label htmlFor="date">Select Month</label>
                            <Calendar
                                id="date"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.value)}
                                view="month"
                                dateFormat="yy-mm"
                                showIcon
                            />
                        </div>

                        <div className="filter-item">
                            <label htmlFor="type">Teacher Type</label>
                            <Dropdown
                                id="type"
                                value={teacherType}
                                options={teacherTypeList}
                                optionLabel="label"
                                optionValue="value"
                                onChange={handleTeacherTypeChange}
                                placeholder="Select Teacher Type"
                            />
                        </div>

                        <div className="filter-item">
                            <label htmlFor="teacherId" title="Search by Sign ID (TID) or NIC">Sign ID (TID) or NIC</label>
                            <InputText
                                id="teacherId"
                                value={teacherId}
                                onChange={(e) => setTeacherId(e.target.value)}
                                placeholder="Search by Sign ID (TID) or NIC"
                                tooltip="Enter Sign ID (TID) or NIC to search"
                            />
                        </div>

                        <div className="filter-item filter-button">
                            <Button
                                label="Search"
                                onClick={handleSearch}
                                className="p-button-primary"
                            />
                        </div>
                    </div>
                </Panel>
                {/* Fixed horizontal scrollbar above paginator */}
                <div ref={scrollBarRef} className="horizontal-scroll-container" style={{ position: "fixed", bottom: "50px", left: "220px", right: 0, height: "18px", overflowX: "auto", overflowY: "hidden", background: "#e9ecef", zIndex: 999, borderTop: "1px solid #dee2e6" }}>
                    <div style={{ width: tableWidth, height: "1px" }}></div>
                </div>
                </div><div style={{ overflowX: "auto", width: "100%" }}>

                <Panel className="data-panel">
                    <DataTable
                        value={attendanceData}
                        onRowClick={(e) => setSelectedRow(selectedRow === e.data ? null : e.data)}
                        rowClassName={(data) => selectedRow === data ? "selected-row" : ""}
                        lazy
                        paginator
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyParams.sortField}
                        sortOrder={lazyParams.sortOrder}
                        rowsPerPageOptions={[50, 100, 200, 500]}
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        loading={loading}
                        scrollable
                        scrollHeight="calc(100vh - 250px)"
                        frozenWidth="250px"
                        style={{ width: '200%', marginTop: '0' }}
                        header={
                            <div style={{ float: 'right' }}>
                                <Button
                                    icon="pi pi-file-pdf"
                                    className="p-button-text export-button"
                                    onClick={handleExport}
                                    tooltip="Export to PDF"
                                />
                            </div>
                        }
                    >
                        <Column
                            field="tid"
                            header="Signing ID"
                            sortable
                            style={{ textAlign: 'center', verticalAlign: 'top', width: '80px' }}
                        />
                        <Column
                            field="teacherSignatureId"
                            header="Sign ID (TID)"
                            style={{ textAlign: 'left', verticalAlign: 'top', width: '110px' }}
                        />
                        <Column
                            field="lastPunchDate"
                            header="Last Punch Date"
                            body={lastPunchDateTemplate}
                            style={{ textAlign: 'left', verticalAlign: 'top', width: '130px' }}
                        />
                        <Column
                            field="teacherName"
                            body={teacherNameTemplate}
                            header="Name"
                            className="teacher-name-column"
                            style={{ textAlign: 'left', verticalAlign: 'top', width: '150px' }}
                        />
                        {dayColumns}
                    </DataTable>
                </Panel>
                {/* Fixed horizontal scrollbar above paginator */}
                <div ref={scrollBarRef} className="horizontal-scroll-container" style={{ position: "fixed", bottom: "50px", left: "220px", right: 0, height: "18px", overflowX: "auto", overflowY: "hidden", background: "#e9ecef", zIndex: 999, borderTop: "1px solid #dee2e6" }}>
                    <div style={{ width: tableWidth, height: "1px" }}></div>
                </div>
                </div>

                <Dialog
                    header="Enter Your Email Address"
                    visible={emailDialogVisible}
                    style={{ width: '50%' }}
                    onHide={() => setEmailDialogVisible(false)}
                >
                    <div className="p-fluid">
                        <InputText
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter email address"
                            style={{ width: '100%', marginBottom: '1rem' }}
                        />
                        <Button
                            label="Send"
                            onClick={handleSendEmail}
                            style={{ marginTop: '5px' }}
                        />
                    </div>
                </Dialog>

                <Dialog
                    header="Enter PIN"
                    visible={pinDialogVisible}
                    style={{ width: '300px' }}
                    onHide={() => { setPinDialogVisible(false); setPin(''); }}
                >
                    <div className="p-fluid">
                        <InputText
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            placeholder="Enter PIN"
                            type="password"
                            style={{ width: '100%', marginBottom: '1rem' }}
                        />
                        <Button
                            label="Submit"
                            onClick={handlePinSubmit}
                            style={{ marginTop: '5px' }}
                        />
                    </div>
                </Dialog>

                <Dialog
                    header="Edit Sign ID (TID)"
                    visible={editTidDialogVisible}
                    style={{ width: '400px' }}
                    onHide={() => { setEditTidDialogVisible(false); setEditingTeacher(null); setNewTid(''); }}
                >
                    <div className="p-fluid">
                        <label style={{ marginBottom: '5px', display: 'block' }}>Teacher: {editingTeacher?.teacherName}</label>
                        <InputText
                            value={newTid}
                            onChange={(e) => setNewTid(e.target.value)}
                            placeholder="Enter new Sign ID (TID)"
                            style={{ width: '100%', marginBottom: '1rem' }}
                        />
                        <Button
                            label="Update"
                            onClick={handleUpdateTid}
                            style={{ marginTop: '5px' }}
                        />
                    </div>
                </Dialog>
            </div>
        </div>
    );
}

export default MonthlyAttendance;
